
<script>
import { layoutMethods, eloadMaintenanceMethods, eloadMaintenanceComputed, liteEloadDropdownMethods, liteEloadDropdownComputed } from "@/state/helpers";
import { required, minLength } from "vuelidate/lib/validators";
import Swal from "sweetalert2";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import Multiselect from "vue-multiselect";
import moment from "moment";

export default {
  page: {
    title: "Eload Maintenance Form",
  },
  validations: {
    eload: {
      network: { minLength: minLength(1) },
      startDate: { required },
      endDate: { required },
      remarks: { required },
    },
  },
  data() {
    return {
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
      filter: null,
      filterOn: [],
      orderBy: "id",
      sort: "DESC",
      sortDesc: true,
      sortBy: "created_at",
      submitted: false,
      isSearching: false,
      eload: {
        network: [],
        startDate: "",
        endDate: "",
        remarks: "",
        status: "",
      },
      networkOptions: [],
      maintenanceStatus: null,
      search: {
        dateRange: "",
      },
      searchValue: {
        searchField: "all",
        search: "",
        dateRange: "",
        page: 1,
        rows: 10,
      },
      searchLogOption: [
        { value: "all", text: "Search All" },
        { value: "actor", text: "Created By" },
      ],
      rangeOptions: {
        mode: "range",
        dateFormat: "Y-m-d",
      },
      isDateRangeValid: true,
      isMainFieldEmpty: false,
      disableDropdown: false,
      showLogsModal: false,
      changePreviewVisible: false,
      changePreviewData: null,
      fields: [
        { key: "created_at", label: "Date & Time", sortable: true },
        { key: "activity", label: "Activity", sortable: true },
        { key: "details", label: "Details", sortable: false },
        { key: "created_by", label: "Created By", sortable: true },
      ],
      baseDateTimeConfig: {
        enableTime: true,
        dateFormat: "Y-m-d h:i K",
        altInput: true,
        altFormat: "F j, Y h:i K",
        time_24hr: false,
      },
      flatpickrCtr: 0,
    };
  },
  components: {
    flatPickr,
    Multiselect,
  },
  
  computed: {
    ...eloadMaintenanceComputed,
    ...liteEloadDropdownComputed,
    
    // Map maintenance logs from store with proper format
    maintenanceLogs() {
      const logs = this.$store.state.eloadMaintenance?.eloadMaintenanceLogs || [];
      return logs.map(log => {
        // Transform backend format to frontend format
        const changes = (log.changes || []).map(change => ({
          field: change.field,
          old: change.oldValue,
          new: change.newValue
        }));
        
        return {
          id: log.id,
          created_at: log.created_at,
          activity: log.remarks,
          details: log.remarks,
          reason: log.reason || null,
          created_by: log.actor,
          changes: changes
        };
      });
    },
    
    isMaintenanceEditable() {
      const id = Number(this.$route.params.id);
      // editable when creating (id === 0) or when status is explicitly Scheduled
      return id === 0 || this.maintenanceStatus === 'Scheduled';
    },
    canShowSaveButton() {
      // Always show save button in add mode (no ID)
      const id = Number(this.$route.params.id);
      // Show save for create page or when maintenance is Scheduled
      // Hide for statuses that should not be editable
      const blockedStatuses = ['Ongoing', 'Force Cancelled', 'Cancelled', 'Ended'];
      if (id === 0) return true;
      if (!this.maintenanceStatus) return false;
      return this.maintenanceStatus === 'Scheduled' && !blockedStatuses.includes(this.maintenanceStatus);
    },
    canShowCancelledButton() {
      return Number(this.$route.params.id) > 0 && this.maintenanceStatus === 'Scheduled';
    },
    canShowForceCancelledButton() {
      return Number(this.$route.params.id) > 0 && this.maintenanceStatus === 'Ongoing';
    },
    startInvalid() {
    return (this.submitted || this.$v.eload.startDate.$dirty) && !this.$v.eload.startDate.required;
    },
    endInvalid() {
      return (
        (this.submitted || this.$v.eload.endDate.$dirty) &&
        (!this.$v.eload.endDate.required )
      );
    },
    startDateTimeConfig() {
      const config = {
        ...this.baseDateTimeConfig,
        clickOpens: this.isMaintenanceEditable,
        allowInput: this.isMaintenanceEditable,
        onChange: this.onStartDateChange,
        altInputClass: `form-control flatpickr-with-icon ${this.startInvalid ? "is-invalid" : ""}`.trim(),
      };
      
      if (Number(this.$route.params.id) === 0 || this.$route.params.id === undefined) {
        // Set minimum date to today at midnight to prevent past dates
        config.minDate = moment().startOf('day').toDate();
        
        // Use minTime to prevent selecting past times on today
        config.minTime = moment().format('HH:mm');
      }
      
      return config;
    },
    endDateTimeConfig() {
      return {
        ...this.baseDateTimeConfig,
        clickOpens: this.isMaintenanceEditable,
        allowInput: this.isMaintenanceEditable,
        // End date cannot be before start date
        minDate: this.eload.startDate || (Number(this.$route.params.id) === 0 || this.$route.params.id === undefined ? 'today' : null),
         altInputClass: `form-control flatpickr-with-icon ${this.endInvalid ? "is-invalid" : ""}`.trim(),
      };
    },
    maintenanceStatusText() {
      if (!this.maintenanceStatus) return '';
      return this.maintenanceStatus;
    },
    maintenanceStatusClass() {
      const statusClasses = {
        'Scheduled': 'text-scheduled',
        'Ongoing': 'text-success',
        'Ended': 'text-ended',
        'Cancelled': 'text-danger',
        'Force Cancelled': 'text-danger'
      };
      return statusClasses[this.maintenanceStatus] || '';
    },
  },
  methods: {
    ...layoutMethods,
    ...eloadMaintenanceMethods,
    ...liteEloadDropdownMethods,
    onNetworkChange(value) {
      if (this.$v && this.$v.eload && this.$v.eload.network) {
        this.$v.eload.network.$touch();
      }
      this.isMainFieldEmpty = !(value && value.length > 0);
    },
    onRemarksInput() {
      if (this.$v && this.$v.eload && this.$v.eload.remarks) {
        this.$v.eload.remarks.$touch();
      }
    },
    async sortOptions(propName, arr) {
      arr.sort((a, b) => {
        const nameA = a[`${propName}`].toUpperCase();
        const nameB = b[`${propName}`].toUpperCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
      return arr;
    },
    async getSKUNetworkList() {
      try {
        await this.fetchLiteNetworksList();
        
        if (this.getLiteNetworksList && this.getLiteNetworksList.length > 0) {
          let sortedList = await this.sortOptions('name', [...this.getLiteNetworksList]);
          this.networkOptions = sortedList.map(network => ({
            id: network.id,
            text: network.name,
            value: network.id
          }));

        }
      } catch (error) {
        console.error('Error loading network list:', error);
        this.$alertify.error('Failed to load network list');
      }
    },
    onStartDateChange() {
      if (this.eload.endDate && this.eload.scheduleStartDate) {
        const startDate = moment(this.eload.scheduleStartDate);
        const endDate = moment(this.eload.endDate);
        
        if (endDate.isBefore(startDate)) {
          this.eload.endDate = null;
        }
      }
    },
    convertTo24HourFormat(dateTimeString) {
      if (!dateTimeString) return null;
      
      const testMoment = moment(dateTimeString, 'YYYY-MM-DD HH:mm', true);
      if (testMoment.isValid()) {
        return dateTimeString;
      }
      const dateMoment = moment(dateTimeString);
      if (!dateMoment.isValid()) return dateTimeString;
      
      return dateMoment.format('YYYY-MM-DD HH:mm');
    },
    async saveMaintenance() {
      this.submitted = true;
      this.$v.$touch();

      this.isMainFieldEmpty = !this.eload.network || this.eload.network.length === 0;

      if (this.isMainFieldEmpty) {
        Swal.fire(
          "Network is required",
          "Please select at least 1 Network to proceed.",
          "warning"
        );
      }

      if (this.isMainFieldEmpty || this.$v.$invalid || !this.isDateRangeValid) {
        return;
      }

      let payload = {
        network: Array.isArray(this.eload.network) ? this.eload.network.map(n => n.value) : [],
        startDate: moment(this.eload.startDate).utc(),
        endDate: moment(this.eload.endDate).utc(),
        remarks: this.eload.remarks,
      };

      if (this.$route.params.id > 0) {
        await this.updateEloadMaintenance({ ...payload, id: this.$route.params.id }).then((res) => {
          if (res.status == "ok") {
            Swal.fire({
              title: 'Updated Successful!',
              text: 'E-Load maintenance is successfully updated.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then((swalRes) => {
              if (swalRes.isConfirmed) {
                this.$router.go(-1);
              }
            });
          }
        });
      } else {
        await this.saveEloadMaintenance(payload).then((res) => {
          if (res.status == "ok") {
            Swal.fire({
              title: 'Successfully Created!',
              text: 'Maintenance network schedule has been saved',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then((swalRes) => {
              if (swalRes.isConfirmed) {
                this.$router.go(-1);
              }
            });
          }
        });
      }
    },

    async validateDate() {
      if (this.eload.startDate && this.eload.endDate) {
        this.isDateRangeValid = moment(this.eload.startDate).isBefore(moment(this.eload.endDate));
      }
    },

    async getMaintenanceLogs() {
      const maintenanceId = Number(this.$route.params.id);
      let params = {
        maintenanceId: maintenanceId,
        page: this.isSearching ? 1 : this.searchValue.page,
        rows: this.searchValue.rows,
        sort: this.sortBy || 'created_at',
        orderDesc: this.sortDesc,
      };

      // Add search params if present (separate, not nested)
      if (this.searchValue.search && this.searchValue.search.trim()) {
        params.search = this.searchValue.search.trim();
        params.searchField = this.searchValue.searchField || null;
      }

      // Add date range if present
      if (this.searchValue.dateRange) {
        params.dateRange = this.searchValue.dateRange;
      }

      try {
        await this.getEloadMaintenanceAuditLogs(params);
        this.isSearching = false;
        if (!this.showLogsModal) {
          this.showLogsModal = true;
        }
      } catch (error) {
        console.error('Error fetching maintenance logs:', error);
        this.$alertify?.error?.('Failed to load maintenance logs');
      }
    },

    async sortList(action) {
      this.sortBy = action.sortBy;
      this.sortDesc = action.sortDesc;
      await this.getMaintenanceLogs();
    },

    onModalOpen() {
      this.searchValue.searchField = "all";
      this.searchValue.search = "";
      this.searchValue.dateRange = "";
      this.searchValue.page = 1;
      this.searchValue.rows = 10;
      this.getMaintenanceLogs();
    },

    formatFieldName(fieldName) {
      const fieldNameMap = {
        'Start Date': 'Maintenance Start Date & Time',
        'End Date': 'Maintenance End Date & Time'
      };
      return fieldNameMap[fieldName] || fieldName;
    },

    formatChangeValue(value, fieldName) {
      const dateFields = ['Start Date', 'End Date'];
      
      if (dateFields.includes(fieldName)) {
        if (value && moment(value).isValid()) {
          return moment.tz(value, 'UTC').tz('Asia/Manila').format('YYYY-DD-MM hh:mm A');
        }
      }
      
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      
      if (value === null || value === undefined) {
        return '-';
      }
      
      return value;
    },

    openChangePreview(item) {
      if (this._changePreviewOutsideHandler) {
        document.removeEventListener('click', this._changePreviewOutsideHandler);
        this._changePreviewOutsideHandler = null;
      }

      // Store data and show preview
      this.changePreviewData = item || null;
      this.changePreviewVisible = true;

      // Setup click-outside handler with proper timing
      this.$nextTick(() => {
        setTimeout(() => {
          this._changePreviewOutsideHandler = (e) => {
            const panel = document.querySelector('.change-preview__inner');
            if (!panel || !this.changePreviewVisible) return;
            
            // Check if click is outside the panel
            if (!panel.contains(e.target)) {
              this.closeChangePreview();
            }
          };
          document.addEventListener('click', this._changePreviewOutsideHandler, true);
        }, 100);
      });
    },

    closeChangePreview() {
      this.changePreviewVisible = false;
      this.changePreviewData = null;

       if (this._changePreviewOutsideHandler) {
        document.removeEventListener('click', this._changePreviewOutsideHandler, true);
        this._changePreviewOutsideHandler = null;
      }
    },

    async onExport() {
      try {
        // Ensure logs are up-to-date
        this.searchValue.page = 1;
        await this.getMaintenanceLogs();

        const total = this.eloadMaintenanceLogsTotal || 0;
        if (total === 0) {
          Swal.fire("Failed!", "No data available to export.", "warning");
          return;
        }

        const maintenanceId = Number(this.$route.params.id);
        const params = {
          email: JSON.parse(localStorage.getItem('userData')).email,
          maintenanceId: maintenanceId,
          page: this.searchValue.page,
          rows: this.searchValue.rows,
          sort: this.sortBy || 'created_at',
          orderDesc: this.sortDesc,
          search: this.searchValue.search,
          searchField: this.searchValue.searchField,
          dateRange: this.searchValue.dateRange,
        };

        await this.exportEloadMaintenanceAuditLogs(params);
        
        Swal.fire(
          "Success!", 
          "Your request for reports is pending. It will be sent to your email once it's ready.", 
          "success"
        );
      } catch (error) {
        console.error('Export failed', error);
        Swal.fire(
          "Failed!",
          "Your request for reports has failed.",
          "warning"
        );
      }
    },


    getStatus() {
      if (!this.eload.startDate || !this.eload.endDate) {
        return null;
      }

      const now = moment();
      const start = moment(this.eload.startDate);
      const end = moment(this.eload.endDate);

      if (this.eload.status === "force-cancelled") {
        return "Force Cancelled";
      } else if (this.eload.status === "cancelled") {
        return "Cancelled";
      } else if (start && now.isBefore(start)) {
        return "Scheduled";
      } else if (start && end && (now.isSameOrAfter(start) && now.isSameOrBefore(end))) {
        return "Ongoing";
      } else if (end && now.isAfter(end)) {
        return "Ended";
      }
      return null;
    },

    async cancelSchedule(scheduleStatus) {
      let messageTitle = '', messageText = '';
      if (scheduleStatus == 'cancelled') {
        messageTitle = 'Cancel Maintenance';
        messageText = 'Are you sure you want to cancel this scheduled maintenance? This action will stop the maintenance before it begins.';
      }
      if (scheduleStatus == 'force-cancelled') {
        messageTitle = 'Force Cancel Maintenance';
        messageText = 'Are you sure you want to force cancel this maintenance? Any ongoing processes will be stopped immediately.';
      }

      const { value: reason, isConfirmed } = await Swal.fire({
        title: messageTitle,
        html: `
          <div style="margin-bottom:14px;font-size: 18px;line-height:1.5;">${messageText}</div>
          <div style="margin-bottom:12px;font-size: 18px;">Please provide a reason for the ${scheduleStatus == 'force-cancelled' ? 'force cancellation' : 'cancellation'}:</div>
          <div style="text-align:left;position:relative;">
            <label style="display:block;margin-bottom:6px;font-size: 16px;font-weight: 500;">Reason<span style="color:#C12927">*</span></label>
            <input id="swal-reason-input" maxlength="50" class="swal2-input" placeholder="Enter reason..." style="width:100%;font-size: 14px;padding:10px 12px;border-radius:4px;height:auto;min-height:38px;border:1px solid #d1d5db;box-shadow:none;color:#333;box-sizing:border-box;text-indent:0;">
            <div id="swal-error-message" style="color:#C12927;font-size:13px;margin-top:4px;display:none;">Reason is required.</div>
            <div style="text-align:right;margin-top:3px;"><small id="swal-reason-counter" style="font-size:12px;color:#9ca3af">0/50</small></div>
          </div>
        `,
        icon: "warning",
        width: 560,
        customClass: {
          popup: 'swal-custom-popup',
          title: 'swal-custom-title',
          content: 'swal-custom-content',
          confirmButton: 'swal-custom-confirm',
          cancelButton: 'swal-custom-cancel',
          icon: 'swal-custom-icon'
        },
        showCancelButton: true,
        confirmButtonColor: "#6f42c1",
        cancelButtonColor: "#C12927",
        confirmButtonText: "Confirm",
        cancelButtonText: "No",
        focusConfirm: false,
        didOpen: () => {
          const input = document.getElementById("swal-reason-input");
          const counter = document.getElementById("swal-reason-counter");
          const errorMsg = document.getElementById("swal-error-message");
          
          if (input && counter) {
            counter.innerText = `${input.value.length}/50`;
            input.addEventListener("input", () => {
              counter.innerText = `${input.value.length}/50`;
              // Hide error message when user starts typing
              if (errorMsg && input.value.trim().length > 0) {
                errorMsg.style.display = 'none';
                input.style.borderColor = '#d1d5db';
              }
            });
            input.focus();
          }
        },
        preConfirm: () => {
          const el = document.getElementById("swal-reason-input");
          const errorMsg = document.getElementById("swal-error-message");
          const val = el ? el.value : "";
          
          if (!val || val.trim().length === 0) {
            if (errorMsg) {
              errorMsg.style.display = 'block';
            }
            if (el) {
              el.style.borderColor = '#C12927';
            }
            return false;
          }
          if (val.length > 50) {
            if (errorMsg) {
              errorMsg.textContent = 'Reason must be 50 characters or less.';
              errorMsg.style.display = 'block';
            }
            if (el) {
              el.style.borderColor = '#C12927';
            }
            return false;
          }
          return val.trim();
        },
      });

      if (!isConfirmed) return;

      try {
        let payload = {
          id: Number(this.$route.params.id),
          status: scheduleStatus == 'force-cancelled' ? 'Force Cancelled' : 'Cancelled',
          reason: reason,
        };

        let response = await this.cancelEloadMaintenance(payload);
        if (response && response.status == "ok") {
          const okText = scheduleStatus == "force-cancelled" ? "The maintenance has been successfully force cancelled." : "The scheduled maintenance has been successfully cancelled.";
          await Swal.fire("Maintenance Cancelled", okText, "success");
          this.$router.push({ name: "Eload Maintenance" });
        }
      } catch (err) {
        console.error('Error cancelling maintenance:', err);
        Swal.fire("Error", "Failed to cancel maintenance", "error");
      }
    }
  },

  async mounted() {
    this.changeTitle({ title: this.$route.params.id > 0 ? "Eload Maintenance View" : "Add E-Load Maintenance" });
    this.changeBreadCrumb({
      breadCrumbs: [
        { text: 'Peddlr', to: { name: 'Home'}},
        { text: 'Peddlr Lite Manager', to: { name: 'Eload Maintenance'} },
        { text: 'E-Load Maintenance Form', to: { name: 'Eload Maintenance'} }
      ]
    });

    await this.getSKUNetworkList();

    if (this.$route.params.id > 0) {
      await this.getEloadMaintenanceList({
        search: {
          field: "id",
          keyword: this.$route.params.id,
        },
        includeAuthor: true
      }).then(() => {
        
        this.eload.network = this.eloadMaintenanceList[0].network ? this.eloadMaintenanceList[0].network.split(",").map((networkId) => {
          return this.networkOptions.find((i) => i.value == Number(networkId));
        }).filter(Boolean) : [];
        this.eload.startDate = moment(this.eloadMaintenanceList[0].start_date).format("YYYY-MM-DD HH:mm");
        this.eload.endDate = moment(this.eloadMaintenanceList[0].end_date).format("YYYY-MM-DD HH:mm");
        this.eload.remarks = this.eloadMaintenanceList[0].remarks || "";
        this.eload.status = this.eloadMaintenanceList[0].status || "";

        if(this.getStatus() != "Scheduled") {
          this.disableDropdown = true;
        }
        
        this.baseDateTimeConfig.minDate = moment(this.eloadMaintenanceList[0].start_date).format("YYYY-MM-DD HH:mm");
        // Calculate status
        this.maintenanceStatus = this.getStatus();

        const title = this.maintenanceStatus === 'Scheduled' ? 'Edit E-Load Maintenance' : 'E-Load Schedule View';
        this.changeTitle({title: title});
      });
    }
  },
  beforeDestroy() {
    if (this._changePreviewOutsideHandler) {
      document.removeEventListener('click', this._changePreviewOutsideHandler, true);
      this._changePreviewOutsideHandler = null;
    }
  },
};
</script>

<template>
  <div class="sku-maintenance-form">
    <div class="row">
      <div class="col-lg-8">
        <div class="card">
          <div class="card-body">
            <div class="mb-3">
              <label>Network<span class="text-danger">*</span></label>
              <div class="position-relative">
                <multiselect
                  :class="['form-control p-0', { 'is-invalid': isMainFieldEmpty}]"
                  v-model="eload.network"
                  placeholder="Select Network"
                  label="text"
                  track-by="value"
                  :options="networkOptions"
                  :multiple="true"
                  :close-on-select="false"
                  :hide-selected="true"
                  :disabled="disableDropdown"
                  @input="onNetworkChange"
                >
                </multiselect>
                <p class="small text-danger mb-0" v-if="isMainFieldEmpty && submitted">Network is required</p>
              </div>
            </div>

            <div class="mb-3 row">
              <div class="col-12 col-md-6">
                <label>Maintenance Start Date & Time <span class="text-danger">*</span></label>
                <div class="position-relative">
                  <flat-pickr
                    :key="`start-${startInvalid ? 1 : 0}-${isMaintenanceEditable ? 1 : 0}`"
                    ref="startPicker"
                    v-model="eload.startDate"
                    :config="startDateTimeConfig"
                    placeholder="Start Date"
                    @input="validateDate()"
                    :disabled="!isMaintenanceEditable"
                  />
                  <i class="bx bx-calendar flatpickr-icon"></i>
                </div>
                <p class="small text-danger mb-0" v-if="startInvalid">Start date is required</p>
              </div>

              <div class="col-12 col-md-6">
                <label>Maintenance End Date & Time <span class="text-danger">*</span></label>
                <div class="position-relative">
                  <flat-pickr
                    :key="`end-${endInvalid ? 1 : 0}-${isMaintenanceEditable ? 1 : 0}`"
                    ref="endPicker"
                    v-model="eload.endDate"
                    :config="endDateTimeConfig"
                    placeholder="End Date"
                    class="form-control flatpickr-with-icon"
                    @input="validateDate()"
                    :disabled="!isMaintenanceEditable"
                  />
                  <i class="bx bx-calendar flatpickr-icon"></i>
                </div>
                <p class="small text-danger mb-0" v-if="endInvalid">End date is required</p>
                <p class="small text-danger mb-0" v-else-if="!isDateRangeValid">End date should be ahead Start date</p>
              </div>
            </div>

            <div class="mb-3">
              <label>Remarks<span class="text-danger">*</span></label>
              <b-form-textarea
                v-model="eload.remarks"
                placeholder="Enter Remarks..."
                rows="3"
                max-rows="6"
                :disabled="!isMaintenanceEditable"
                :class="{ 'is-invalid': ($v && $v.eload && $v.eload.remarks) && ($v.eload.remarks.$dirty || submitted) && !$v.eload.remarks.required }"
                @input="onRemarksInput"
              >
              </b-form-textarea>
              <p class="small text-danger mb-0" v-if="($v && $v.eload && $v.eload.remarks) && ($v.eload.remarks.$dirty || submitted) && !$v.eload.remarks.required">Remarks is required</p>
            </div>

            <!-- Status Display -->
            <div v-if="$route.params.id > 0 && maintenanceStatus" class="mb-3">
              <p class="mb-0">
                <span class="fw-bold">Status: </span> 
                <span :class="maintenanceStatusClass" class="fw-bold">{{ maintenanceStatusText }}</span>
              </p>
            </div>

            <!-- Change Log Link -->
            <div v-if="$route.params.id > 0" class="mb-2">
              <p class="mb-0">
                Change Log: <a href="#" @click.prevent="onModalOpen" class="view-link">View</a>
              </p>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex justify-content-between align-items-center mt-4">
              <div>
                <!-- Show Cancelled button for Scheduled status -->
                <b-button 
                  v-if="canShowCancelledButton"
                  variant="outline-danger" 
                  class="w-sm" 
                  type="button"
                  @click="cancelSchedule('cancelled')">
                    Cancelled
                </b-button>
                
                <!-- Show Force Cancelled button for Ongoing status -->
                <b-button 
                  v-if="canShowForceCancelledButton"
                  variant="outline-danger" 
                  class="w-sm" 
                  type="button"
                  @click="cancelSchedule('force-cancelled')">
                    Force Cancelled
                </b-button>
              </div>
              
              <div class="d-flex gap-2">
                <!-- Show Save/Update button only if maintenance is editable -->
                <b-button 
                  v-if="canShowSaveButton"
                  variant="primary" 
                  class="px-5" 
                  type="button"
                  @click="saveMaintenance()">
                    {{ $route.params.id > 0 ? 'Update' : 'Save' }}
                </b-button>
                
                <router-link :to="{ name: 'Eload Maintenance' }" class="tc-white">
                  <b-button variant="light" class="px-5" type="button">Cancel</b-button>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Maintenance Logs Modal -->
    <b-modal
      v-model="showLogsModal"
      id="maintenance-logs-modal"
      title="E-Load Maintenance Logs"
      size="xl"
      hide-header-close
      centered
      modal-class="maintenance-log-modal"
    >
      <template #modal-title>
        E-Load Maintenance Logs
      </template>
      <template #modal-footer>
        <div class="w-100 text-end">
          <b-button variant="light" class="px-5" @click="showLogsModal = false">Close</b-button>
        </div>
      </template>

      <div class="row align-items-start g-0 mt-2 mb-3">
        <div class="col-sm-2">
          <b-form-group label-for="search-type" class="text-end">
            <b-form-select
              v-model="searchValue.searchField"
              class="form-select rounded-0 shadow-none w-100"
              :options="searchLogOption"
            >
            </b-form-select>
          </b-form-group>
        </div>

        <div class="col-sm-3">
          <b-form-group label-for="search-keyword" description="Search Keyword" class="text-end">
            <input
              class="form-control rounded-0 shadow-none w-100"
              v-model="searchValue.search"
              placeholder="Search Keyword"
              @keydown.enter.prevent="getMaintenanceLogs"
            />
          </b-form-group>
        </div>

        <div class="col-sm-3 ms-3">
          <b-form-group label-for="date-range" description="Filter by date" class="text-end">
            <flat-pickr
              v-model="searchValue.dateRange"
              :config="rangeOptions"
              placeholder="Select date range"
              class="form-control rounded-0 shadow-none w-100"
            ></flat-pickr>
          </b-form-group>
        </div>

        <div class="col d-flex justify-content-end align-items-center buttons-group gap-1 flex-wrap">
          <b-button variant="primary" type="button" @click="getMaintenanceLogs" class="h-fit d-flex align-items-center justify-content-center me-2 w-fit">
            <i class="bx bx-search me-1 mt-1"></i>Search
          </b-button>

          <b-button variant="primary" @click="onExport" type="button" class="px-3 d-flex align-items-center">
            <i class='uil uil-export me-2'></i>
            Export
          </b-button>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12">
          <!-- table responsive -->
          <div class="table-responsive">
            <div class="table align-middle table-nowrap">
              <b-table
                :items="maintenanceLogs"
                :fields="fields"
                responsive="sm"
                :current-page="searchValue.page"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :no-local-sorting="true"
                @sort-changed="sortList"
              >
                <template #cell(details)="data">
                  <span v-if="data.item.reason" :id="'reason-' + data.item.id" class="d-inline-block">
                    <a href="#" class="view-link" @click.prevent="void 0">View reason</a>
                    <b-tooltip
                      :target="'reason-' + data.item.id"
                      triggers="hover focus"
                      placement="top"
                      boundary="viewport"
                      :title="data.item.reason">
                    </b-tooltip>
                  </span>
                  <a v-else-if="data.item.changes && data.item.changes.length > 0" href="#" class="view-link" @click.prevent="openChangePreview(data.item, $event)">View changes</a>
                </template>
                
                <template #cell(created_at)="data">
                  <span>{{ data.item.created_at | formatDate }}</span>
                </template>
              </b-table>
            </div>
          </div>
          
          <!-- change preview floating panel -->
          <div v-if="changePreviewVisible" class="change-preview">
            <div class="change-preview__inner">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <h6 class="mb-0 text-white fw-bold">Edited Fields:</h6>
                <button class="btn-close" @click="closeChangePreview">&times;</button>
              </div>
              <!-- show changePreviewData.changes array as Before/After rows -->
              <div v-if="changePreviewData && changePreviewData.changes && changePreviewData.changes.length > 0">
                <table class="table table-sm table-change mb-0">
                  <thead>
                    <tr>
                      <th style="width: 35%">Field</th>
                      <th style="width: 65%">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(c, idx) in changePreviewData.changes" :key="idx">
                      <td class="align-top text-white">{{ formatFieldName(c.field) }}</td>
                      <td>
                        <div class="before-line text-white">
                          Before:
                          <span class="ms-1">{{ formatChangeValue(c.old, c.field) }}</span>
                        </div>
                        <div class="after-line text-white mt-2">
                          After:
                          <span class="ms-1">{{ formatChangeValue(c.new, c.field) }}</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-white text-center py-3">
                <p class="mb-0">No detailed changes available.</p>
              </div>
            </div>
          </div>
          
          <div class="row g-0 text-center" v-if="eloadMaintenanceLogsTotal == 0">
            <span>No data to display.</span>
          </div>
          
          <div class="row g-0 align-items-center" v-if="eloadMaintenanceLogsTotal > 0">
            <div class="col-auto">
              <div class="d-flex align-items-center">
                <label class="me-2 mb-0 col-form-label">Display</label>
                <b-form-select 
                  v-model="searchValue.rows" 
                  :options="pageOptions" 
                  class="form-select shadow-none w-fit" 
                  @change="getMaintenanceLogs"
                ></b-form-select>
              </div>
            </div>
            <div class="col" v-if="eloadMaintenanceLogsTotal > searchValue.rows">
              <div class="float-end">
                <b-pagination
                  class="pagination-rounded mb-0"
                  v-model="searchValue.page"
                  @input="getMaintenanceLogs"
                  :total-rows="eloadMaintenanceLogsTotal"
                  :per-page="searchValue.rows"
                ></b-pagination>
              </div>
            </div>
          </div>
        </div><!--end of col-12-->
      </div><!--end of row-->
    </b-modal>
    <!-- END Maintenance Logs Modal -->
  </div>
</template>

<style>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

a.disabled {
  pointer-events: none;
}

.personal-details-widget {
  background-color: #e6eaf1;
}

.multiselect.is-invalid .multiselect__tags {
  padding-right: calc(1.5em + 0.94rem) !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23f34e4e'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23f34e4e' stroke='none'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right calc(1.875em + 0.235rem) center;
  background-size: calc(0.75em + 0.47rem) calc(0.75em + 0.47rem);
}

.invalid ~ .invalid-feedback {
  display: block;
}

/* .multiselect__option--highlight,
.multiselect__option--highlight::after {
  background: #f8f9fa;
  color: #495057;
} */

.multiselect__tag {
  background-color: #e73092 !important;
  border-color: #e73092 !important;
  margin-bottom: 0 !important;
  font-weight: 400 !important;
}

.multiselect__tag-icon:after {
  color: #fff !important;
}

.multiselect__tag-icon:focus,
.multiselect__tag-icon:hover {
  background-color: #e73092 !important;
}


.sku-maintenance-form .multiselect--disabled .multiselect__tags {
  background-color: #f5f6f8 !important;
  cursor: not-allowed;
  opacity: 1;
}


.sku-maintenance-form .form-control.flatpickr-input[disabled],
.sku-maintenance-form .form-control.flatpickr-input:disabled {
  background-color: #e9ecef !important;
  opacity: 1;
  cursor: not-allowed;
  color: #6c757d;
  pointer-events: none;
}



.sku-maintenance-form .flatpickr-input[disabled] ~ .form-control,
.sku-maintenance-form .flatpickr-input:disabled ~ .form-control,
.sku-maintenance-form .form-control.form-control-input[disabled],
.sku-maintenance-form .form-control.form-control-input:disabled,
.sku-maintenance-form input.form-control[readonly][disabled] {
  background-color: #e9ecef !important;
  opacity: 1;
  cursor: not-allowed;
  color: #6c757d;
  pointer-events: none;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Multiselect validation styling */
::v-deep .multiselect.is-invalid .multiselect__tags {
  border-color: #f34e4e !important;
}

/* Ensure validation icon appears above flatpickr icon */
.sku-maintenance-form .validation-icon-date {
  right: 12px;
  left: auto;
}

.text-scheduled {
  color: #4D56FF !important;
}

.text-success {
  color: #508F30 !important;
}

.text-ended {
  color: #AFAFAF !important;
}

.text-danger {
  color: #C12928 !important;
}

.flatpickr-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: #6c757d;
  pointer-events: none;
  z-index: 1;
}

.flatpickr-with-icon {
  padding-left: 40px !important;
}

.flatpickr-with-icon ~ .flatpickr-icon {
  display: block;
}

.view-link {
  color: #e935a1;
  font-weight: 600;
  text-decoration: underline !important;
}
.view-link:hover {
  text-decoration: underline;
}

.change-preview {
  position: fixed;
  left: 50%;
  top: 18%;
  transform: translateX(-50%);
  z-index: 2050;
  background: transparent;
  padding: 0;
  pointer-events: none;
}

.change-preview__inner {
  background: #0f1112 !important;
  border-radius: 12px;
  padding: 22px 26px;
  width: 720px;
  max-width: 94vw;
  max-height: 70vh;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.04);
  pointer-events: auto;
}

.table-change thead th {
  background: #0f1112 !important;
  color: #ffffff !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.913);
  font-weight: 700;
  font-size: 14px;
  padding: 10px 12px;
  text-align: left;
}

.table-change td {
  background: #0f1112 !important;
  color: #e6e6e6 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.654);
  padding: 11px 12px;
  font-size: 13px;
  line-height: 1.4;
}

.table-change tbody tr:last-child td {
  border-bottom: none;
}

.table-change tbody tr:hover {
  background: rgba(255, 255, 255, 0.02);
}

.change-preview__inner .table-change td {
  padding: 8px 18px;
  vertical-align: top;
}

.change-preview__inner .table-change thead th {
  padding: 12px 18px;
  border-bottom: 1px solid rgba(255,255,255,0.12);
}

.change-preview__inner .before-line strong,
.change-preview__inner .after-line strong {
  color: #e6e6e6;
}

.btn-close {
  background: transparent;
  border: 0;
  color: #ffffff;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  opacity: 0.85;
  transition: opacity 0.12s;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close:hover { 
  opacity: 1; 
}

::v-deep .maintenance-log-modal .modal-content {
  border-radius: 12px;
  padding: 22px 24px;
}

::v-deep .maintenance-log-modal .modal-dialog {
  max-width: 900px;
  width: 100%;
}

@media (max-width: 992px) {
  ::v-deep .maintenance-log-modal .modal-dialog { 
    max-width: 720px; 
  }
}

@media (max-width: 768px) {
  ::v-deep .maintenance-log-modal .modal-dialog { 
    max-width: 95vw; 
    margin: 0.5rem; 
  }
  ::v-deep .maintenance-log-modal .modal-content { 
    padding: 14px; 
  }
}

@media (max-width: 576px) {
  .maintenance-log-modal .row.align-items-start.g-0 > .col-sm-2,
  .maintenance-log-modal .row.align-items-start.g-0 > .col-sm-3,
  .maintenance-log-modal .row.align-items-start.g-0 > .col-sm-1 {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .maintenance-log-modal .b-form-group.text-end {
    text-align: left !important;
  }

  .maintenance-log-modal .ms-3 { 
    margin-left: 0 !important; 
  }

  .maintenance-log-modal .buttons-group {
    justify-content: flex-end;
  }

  .maintenance-log-modal .buttons-group .btn {
    white-space: nowrap;
  }
}

.logs-controls {
  gap: 8px;
  flex-wrap: wrap;
}

.logs-controls .form-control,
.logs-controls .form-select {
  height: 40px;
  border-radius: 8px;
}

::v-deep .table thead th {
  font-weight: 600;
  color: #111827;
}

::v-deep .table tbody td {
  color: #111827;
  vertical-align: middle;
}

::v-deep .pagination-rounded .page-item.active .page-link {
  background: #804997;
  border-color: #804997;
}

::v-deep .btn-outline-primary:hover,
::v-deep .btn-export-logs:hover {
  background: #e935a1;
  border-color: #e935a1;
  color: #ffffff;
}

::v-deep .modal-footer {
  border-top: none !important;
  padding-top: 0.2rem !important;
  padding-bottom: 0.2rem !important;
}

/* Popup */
.swal-custom-popup {
  border-radius: 10px !important;
  padding: 28px 32px 32px !important;
}

/* Icon circle */
.swal-custom-icon.swal2-icon.swal2-warning {
  border: 3px solid #FEF3E2 !important;
  color: #F5B342 !important;
  width: 65px !important;
  height: 65px !important;
  line-height: 61px !important;
  margin: 0 auto 16px !important;
}

.swal-custom-title {
  font-size: 20px !important;
  font-weight: 600 !important;
  margin-top: 0 !important;
  margin-bottom: 14px !important;
  text-align: center !important;
  color: #1a202c !important;
}

.swal-custom-content {
  text-align: center !important;
  font-size: 14px !important;
  color: #4a5568 !important;
  line-height: 1.5 !important;
}

/* input already styled inline; ensure it doesn't overflow */
.swal2-html-container input.swal2-input,
.swal2-html-container textarea.swal2-textarea {
  box-shadow: none !important;
  margin: 0 !important;
}

/* Buttons */
.swal-custom-confirm {
  background: #7c3aed !important;
  color: #fff !important;
  border-radius: 5px !important;
  padding: 9px 28px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  margin: 0 8px 0 0 !important;
  border: none !important;
}
.swal-custom-confirm:hover {
  background: #6d28d9 !important;
}
.swal-custom-cancel {
  background: #dc2626 !important;
  color: #fff !important;
  border-radius: 5px !important;
  padding: 9px 22px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  margin: 0 !important;
  border: none !important;
}
.swal-custom-cancel:hover {
  background: #b91c1c !important;
}

/* counter */
#swal-reason-counter {
  color: #9ca3af;
}

.sku-maintenance-form .flatpickr-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: #6c757d;
  pointer-events: none;
  z-index: 1;
}

.sku-maintenance-form .flatpickr-with-icon {
  padding-left: 40px !important;
}

.sku-maintenance-form .flatpickr-with-icon ~ .flatpickr-icon {
  display: block;
}
::v-deep input.is-invalid {
  border-color: #f34e4e !important;
  box-shadow: none !important;
}
</style>
